import { useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Menu } from 'lucide-react';
import { Sheet, SheetTrigger } from '@/components/ui/sheet';
import { OwnerSheetContent } from '@/components/owner-ui';
import { cn } from '@/lib/utils';
import { isNavPathActive, ownerPath } from '@/lib/hosts';
import { useTenantHeaderNotifications } from '@/hooks/queries/useHeaderNotifications';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';
import { useVisibleNavItems } from '@/hooks/useVisibleNavItems';
import { rentalWorkspaceLabel } from '@/lib/rental-labels';
import { MobileDrawerTwoPane } from './MobileDrawerTwoPane';

/**
 * Mobile rail drawer — Phase 8.3 (HeyGen mobile pattern).
 *
 * Trigger : a horizontal "module + burger" cluster glued top-left (HeyGen-style).
 * - Left tile  : current module icon on a lime accent background (acts as a
 *   visual indicator of the section the user is currently in)
 * - Right tile : burger lines + red notification dot when unread > 0
 *
 * The whole cluster is a single button. Tap opens an `OwnerSheetContent` from
 * the left containing the two-pane drawer (icon rail + contextual sub-nav).
 */
export function MobileRailDrawer() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useTranslation('navigation');
  const visibleItems = useVisibleNavItems();
  const { data: shopDetailResponse } = useCurrentShopDetail();
  const shopDetail = shopDetailResponse?.data;
  const notifications = useTenantHeaderNotifications();
  const unread = notifications.unreadCount;

  // Resolve the current section (matching item) so the left tile reflects context.
  const currentSection = useMemo(() => {
    const stripped = pathname.startsWith('/owner')
      ? pathname.slice('/owner'.length) || '/'
      : pathname;
    return (
      visibleItems.find(
        (item) =>
          isNavPathActive(pathname, ownerPath(item.href)) || isNavPathActive(stripped, item.href),
      ) ?? null
    );
  }, [pathname, visibleItems]);

  const SectionIcon = currentSection?.icon ?? LayoutDashboard;
  const sectionLabel =
    currentSection?.labelKey === 'rental'
      ? rentalWorkspaceLabel({
          category: shopDetail?.category,
          profile: shopDetail?.rental_profile,
        })
      : currentSection
        ? t(currentSection.labelKey, { defaultValue: currentSection.labelKey })
        : t('dashboard', { defaultValue: 'Tableau de bord' });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={`${sectionLabel} — ${t('open_menu', { defaultValue: 'Ouvrir le menu' })}`}
          className={cn(
            'fixed left-3 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] z-30 flex items-center gap-1 rounded-2xl border border-slate-200/80 bg-white/90 p-1 shadow-[0_4px_16px_-2px_rgba(15,23,42,0.08)] backdrop-blur-md transition-colors lg:hidden',
            'hover:bg-white dark:border-charcoal-700/70 dark:bg-charcoal-900/90 dark:hover:bg-charcoal-900',
          )}
        >
          <span
            aria-hidden="true"
            className="grid h-8 w-8 place-items-center rounded-xl bg-lime-100 text-charcoal-950 dark:bg-lime-500/20 dark:text-lime-300"
          >
            <SectionIcon className="h-4 w-4" strokeWidth={2} />
          </span>
          <span
            aria-hidden="true"
            className="relative grid h-8 w-8 place-items-center rounded-xl text-slate-700 dark:text-slate-300"
          >
            <Menu className="h-4 w-4" strokeWidth={2} />
            {unread > 0 ? (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-charcoal-900" />
            ) : null}
          </span>
        </button>
      </SheetTrigger>
      <OwnerSheetContent
        side="left"
        showCloseButton
        className="w-[92vw] max-w-[380px] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <MobileDrawerTwoPane onNavigate={() => setOpen(false)} />
      </OwnerSheetContent>
    </Sheet>
  );
}
